const ADMIN_TENANT_VIEW_STORAGE_KEY = 'gesty.adminTenantView';
const ADMIN_TENANT_HANDOFF_STORAGE_KEY = 'gesty.adminTenantHandoff';
const ADMIN_TENANT_AUTH_STORAGE_KEY = 'gesty.adminTenantAuth';
export const ADMIN_TENANT_VIEW_SESSION_CLEARED_EVENT =
  'gesty:admin-tenant-view-session-cleared';

const TENANT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ADMIN_HANDOFF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;
const ADMIN_ACCESS_TOKEN_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

type StoredAdminTenantView = {
  tenantId: string;
  viewExpiresAt: string;
};

type StoredAdminTenantAuth = {
  accessToken: string;
  expiresAt: string;
};

export function hasActivePlatformAdminTenantViewAccess(): boolean {
  return Boolean(readStoredAdminTenantView());
}

export function hasPlatformAdminTenantViewExternalAuth(): boolean {
  return Boolean(readStoredAdminTenantView() && readStoredAdminTenantAuth());
}

export function clearPlatformAdminTenantViewSession(): void {
  if (typeof window === 'undefined') return;
  for (const key of [
    ADMIN_TENANT_VIEW_STORAGE_KEY,
    ADMIN_TENANT_HANDOFF_STORAGE_KEY,
    ADMIN_TENANT_AUTH_STORAGE_KEY,
  ]) {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Ignore restricted storage; callers will still force a fresh navigation.
    }
  }
  window.dispatchEvent(new Event(ADMIN_TENANT_VIEW_SESSION_CLEARED_EVENT));
}

export function hasPlatformAdminTenantHandoffIntent(): boolean {
  return Boolean(readStoredAdminTenantView() || readStoredAdminHandoffToken());
}

function readStoredAdminTenantView(): StoredAdminTenantView | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const persisted = window.sessionStorage.getItem(ADMIN_TENANT_VIEW_STORAGE_KEY);
    if (!persisted) return undefined;

    if (isTenantId(persisted)) {
      return {
        tenantId: persisted,
        viewExpiresAt: getDefaultAdminTenantViewExpiresAt(),
      };
    }

    const parsed = JSON.parse(persisted) as Partial<StoredAdminTenantView>;
    if (isTenantId(parsed.tenantId) && isFutureTimestamp(parsed.viewExpiresAt)) {
      return {
        tenantId: parsed.tenantId,
        viewExpiresAt: parsed.viewExpiresAt,
      };
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function readStoredAdminTenantAuth(): StoredAdminTenantAuth | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const persisted = window.sessionStorage.getItem(ADMIN_TENANT_AUTH_STORAGE_KEY);
    if (!persisted) return undefined;
    const parsed = JSON.parse(persisted) as Partial<StoredAdminTenantAuth>;
    if (isAdminAccessToken(parsed.accessToken) && isFutureTimestamp(parsed.expiresAt)) {
      return {
        accessToken: parsed.accessToken,
        expiresAt: parsed.expiresAt,
      };
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function readStoredAdminHandoffToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const persisted = window.sessionStorage.getItem(ADMIN_TENANT_HANDOFF_STORAGE_KEY);
    return isAdminHandoffToken(persisted) ? persisted : undefined;
  } catch {
    return undefined;
  }
}

function isTenantId(value: unknown): value is string {
  return typeof value === 'string' && TENANT_ID_PATTERN.test(value);
}

function isAdminHandoffToken(value: unknown): value is string {
  return typeof value === 'string' && ADMIN_HANDOFF_TOKEN_PATTERN.test(value);
}

function isAdminAccessToken(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 64 &&
    value.length <= 4096 &&
    ADMIN_ACCESS_TOKEN_PATTERN.test(value)
  );
}

function isFutureTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

function getDefaultAdminTenantViewExpiresAt(): string {
  return new Date(Date.now() + 60 * 60 * 1000).toISOString();
}
